import React from 'react';
import LegalLayout, { TocSection } from '../../components/legal/LegalLayout';
import { useLanguage } from '../../context/LanguageContext';

const CookiePolicy: React.FC = () => {
  const { language } = useLanguage();

  if (language === 'ar') {
    const arSections: TocSection[] = [
      { id: 'cerez-nedir', title: '1. ما هي ملفات تعريف الارتباط؟' },
      { id: 'cerez-turleri', title: '2. أنواع ملفات تعريف الارتباط المستخدمة' },
      { id: 'cerez-listesi', title: '3. قائمة ملفات تعريف الارتباط التفصيلية' },
      { id: 'cerez-yonetimi', title: '4. إدارة وتعديل تفضيلات الكوكيز' },
      { id: 'politikadaki-degisiklikler', title: '5. التحديثات والتواصل' },
    ];

    return (
      <LegalLayout
        title="سياسة ملفات تعريف الارتباط (الكوكيز)"
        subtitle="دليل معلومات وإدارة ملفات تعريف الارتباط"
        lastUpdated="01 سبتمبر 2026"
        activeTab="cookies"
        sections={arSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              🍪
            </span>
            <span>إشعار ملفات تعريف الارتباط</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            في حركة المرأة الحرة (TJA)، نستخدم ملفات تعريف الارتباط (الكوكيز) أثناء زيارتكم لموقعنا الإلكتروني لتعزيز تجربة التصفح، وضمان عمل الموقع بكفاءة، وتذكر تفضيلاتكم اللغوية.
          </p>
        </div>

        {/* Section 1 */}
        <section id="cerez-nedir" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">01.</span>
            ما هي ملفات تعريف الارتباط ولماذا تُستخدم؟
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            ملفات تعريف الارتباط هي ملفات نصية صغيرة يتم تخزينها على جهازكم (الكمبيوتر، الهاتف الذكي، الجهاز اللوحي) عبر المتصفح عند زيارة أي موقع ويب.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            تساعد هذه الملفات في تشغيل الموقع بشكل آمن وفعال، وتذكر لغتكم المختارة وبيانات الجلسة لتوفير تجربة تصفح سلسة ومخصصة.
          </p>
        </section>

        {/* Section 2 */}
        <section id="cerez-turleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">02.</span>
            أنواع ملفات تعريف الارتباط المستخدمة في موقعنا
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            يستخدم موقعنا فقط ملفات تعريف الارتباط الضرورية للوظائف الأساسية والأداء:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
                🔒
              </div>
              <h4 className="font-bold text-brand-purple-900 text-base">1. ملفات تعريف الارتباط الإلزامية / التقنية</h4>
              <p className="text-xs text-gray-500 leading-relaxed font-light">
                ضرورية للغاية لعمل الموقع وأمانه، وتذكر لغتكم المفضلة (العربية، التركية، الكردية، الإنجليزية). قد تتوقف بعض وظائف الموقع في حال تعطيلها.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
                📊
              </div>
              <h4 className="font-bold text-brand-purple-900 text-base">2. ملفات تعريف الارتباط التحليلية</h4>
              <p className="text-xs text-gray-500 leading-relaxed font-light">
                تساعدنا في فهم كيفية تفاعل الزوار مع صفحات الموقع والتقارير بشكل مجهول الهوية تماماً دون جمع أي بيانات هوية شخصية.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section id="cerez-listesi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">03.</span>
            قائمة ملفات تعريف الارتباط التفصيلية ووظائفها
          </h2>
          <div className="rounded-2xl border border-brand-purple-100/80 overflow-hidden shadow-sm bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm border-collapse">
                <thead className="bg-brand-purple-900 text-white font-serif text-xs uppercase tracking-wider">
                  <tr>
                    <th className="py-4 px-6 w-1/4">اسم الكوكيز</th>
                    <th className="py-4 px-6 w-1/6">الفئة</th>
                    <th className="py-4 px-6">الوظيفة</th>
                    <th className="py-4 px-6 w-1/5">مدة الاحتفاظ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-600 font-light text-xs">
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">i18nextLng</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                        إلزامي
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">يحفظ تفضيل اللغة التي اخترتها لتوفير تجربة تصفح متسقة.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">سنة واحدة</td>
                  </tr>
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">_ga / _gid</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200/60 rounded-full font-semibold text-[11px]">
                        تحليلات
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">إحصائيات استخدام مجهولة الهوية عبر Google Analytics.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">سنتان / 24 ساعة</td>
                  </tr>
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">session_id</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                        إلزامي
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">يضمن أمان واستمرارية جلسة التصفح.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">مدة الجلسة</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="cerez-yonetimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">04.</span>
            إدارة وتعديل تفضيلات الكوكيز
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            يمكنكم تغيير تفضيلات ملفات تعريف الارتباط أو حذفها أو حظرها بالكامل في أي وقت من خلال إعدادات المتصفح الخاص بكم:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Google Chrome</h4>
              <p className="text-xs text-gray-500 font-light">
                الإعدادات ← الخصوصية والأمان ← ملفات تعريف الارتباط وبيانات الموقع الأخرى.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Mozilla Firefox</h4>
              <p className="text-xs text-gray-500 font-light">
                الإعدادات ← الخصوصية والأمان ← الكوكيز وبيانات الموقع.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Apple Safari</h4>
              <p className="text-xs text-gray-500 font-light">
                التفضيلات ← الخصوصية ← حظر جميع ملفات تعريف الارتباط.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Microsoft Edge</h4>
              <p className="text-xs text-gray-500 font-light">
                الإعدادات ← أذونات الموقع ← ملفات تعريف الارتباط وبيانات الموقع.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section id="politikadaki-degisiklikler" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">05.</span>
            التحديثات والتواصل
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            قد نقوم بتحديث سياسة ملفات تعريف الارتباط هذه بما يتوافق مع المتطلبات القانونية والتقنية. تسري أي تعديلات فور نشرها على الموقع.
          </p>
          <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-brand-purple-900 text-sm">لأي استفسارات بخصوص الكوكيز:</h4>
              <p className="text-xs text-gray-500 font-light">يمكنكم التواصل معنا عبر البريد الإلكتروني.</p>
            </div>
            <a
              href="mailto:tja.bilgi@gmail.com"
              className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
            >
              tja.bilgi@gmail.com
            </a>
          </div>
        </section>
      </LegalLayout>
    );
  }

  if (language === 'en') {
    const enSections: TocSection[] = [
      { id: 'cerez-nedir', title: '1. What Are Cookies and Why Are They Used?' },
      { id: 'cerez-turleri', title: '2. Types of Cookies Used on Our Website' },
      { id: 'cerez-listesi', title: '3. Detailed Cookie Inventory and Functions' },
      { id: 'cerez-yonetimi', title: '4. Managing and Disabling Cookie Preferences' },
      { id: 'politikadaki-degisiklikler', title: '5. Policy Updates and Contact' },
    ];

    return (
      <LegalLayout
        title="Cookie Policy"
        subtitle="Cookie Information and Management Guide"
        lastUpdated="September 01, 2026"
        activeTab="cookies"
        sections={enSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              🍪
            </span>
            <span>Cookie Notice</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            As the Free Women's Movement (TJA) / Women's Solidarity Network, we use cookies during your visit to our website to enhance your user experience, ensure website functionality, and remember your preferences.
          </p>
        </div>

        {/* Section 1 */}
        <section id="cerez-nedir" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
            What Are Cookies and Why Are They Used?
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Cookies are small text files stored on your device (computer, smartphone, tablet) via your browser when you visit a website.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Cookies ensure the website operates more efficiently, remember your preferred language or session details, and provide a seamless, personalized browsing experience.
          </p>
        </section>

        {/* Section 2 */}
        <section id="cerez-turleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
            Types of Cookies Used on Our Website
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Our website uses only necessary technical and analytical cookies:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
                🔒
              </div>
              <h4 className="font-bold text-brand-purple-900 text-base">1. Essential / Technical Cookies</h4>
              <p className="text-xs text-gray-500 leading-relaxed font-light">
                Mandatory for the fundamental functionality and security of the site, as well as preserving language preferences (English, Turkish, Kurdish, Arabic). If disabled, certain parts of the site may not function properly.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
                📊
              </div>
              <h4 className="font-bold text-brand-purple-900 text-base">2. Analytical Cookies</h4>
              <p className="text-xs text-gray-500 leading-relaxed font-light">
                Used to understand how visitors interact with our pages and reports anonymously, without collecting any personally identifiable information.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section id="cerez-listesi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
            Detailed Cookie Inventory and Functions
          </h2>
          <div className="rounded-2xl border border-brand-purple-100/80 overflow-hidden shadow-sm bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-brand-purple-900 text-white font-serif text-xs uppercase tracking-wider">
                  <tr>
                    <th className="py-4 px-6 w-1/4">Cookie Name</th>
                    <th className="py-4 px-6 w-1/6">Category</th>
                    <th className="py-4 px-6">Purpose</th>
                    <th className="py-4 px-6 w-1/5">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-600 font-light text-xs">
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">i18nextLng</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                        Essential
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">Stores your selected interface language for consistent browsing across visits.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">1 Year</td>
                  </tr>
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">_ga / _gid</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200/60 rounded-full font-semibold text-[11px]">
                        Analytics
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">Anonymous usage statistics via Google Analytics to help us improve content.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">2 Years / 24 Hours</td>
                  </tr>
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">session_id</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                        Essential
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">Maintains browsing session security and integrity.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="cerez-yonetimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
            Managing and Disabling Cookie Preferences
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            You can modify, block, or delete cookie preferences at any time through your browser settings:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Google Chrome</h4>
              <p className="text-xs text-gray-500 font-light">
                Settings → Privacy & Security → Cookies and other site data.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Mozilla Firefox</h4>
              <p className="text-xs text-gray-500 font-light">
                Settings → Privacy & Security → Cookies and Site Data.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Apple Safari</h4>
              <p className="text-xs text-gray-500 font-light">
                Preferences → Privacy → Block all cookies.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Microsoft Edge</h4>
              <p className="text-xs text-gray-500 font-light">
                Settings → Cookies and site permissions → Cookies and data stored.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section id="politikadaki-degisiklikler" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
            Policy Updates and Contact
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            We may update this Cookie Policy in accordance with legal and technical requirements. Any changes will become effective immediately upon being published on our website.
          </p>
          <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-brand-purple-900 text-sm">For questions regarding our Cookie Policy:</h4>
              <p className="text-xs text-gray-500 font-light">You can contact us via email.</p>
            </div>
            <a
              href="mailto:tja.bilgi@gmail.com"
              className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
            >
              tja.bilgi@gmail.com
            </a>
          </div>
        </section>
      </LegalLayout>
    );
  }

  if (language === 'krm') {
    const krmSections: TocSection[] = [
      { id: 'cerez-nedir', title: '1. Çerez Çi ne û Çima Tên Bikaranîn?' },
      { id: 'cerez-turleri', title: '2. Cûreyên Çerezên ku li ser Malpera Me Tên Bikaranîn' },
      { id: 'cerez-listesi', title: '3. Lîsteya Berfireh a Çerezan û Erkên Wan' },
      { id: 'cerez-yonetimi', title: '4. Birêvebirin û Astengkirina Vebijêrkên Çerezan' },
      { id: 'politikadaki-degisiklikler', title: '5. Nûvekirinên Siyasetê û Têkilî' },
    ];

    return (
      <LegalLayout
        title="Siyaseta Çerezan"
        subtitle="Rêbernameya Agahdarî û Birêvebirina Çerezan"
        lastUpdated="01 Êlûn 2026"
        activeTab="cookies"
        sections={krmSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              🍪
            </span>
            <span>Agahdariya Çerezan</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            Wek Tevgera Jinên Azad (TJA) / Tora Piştevaniya Jinan, di dema serdana we ya malpera me de ji bo pêşxistina ezmûna we ya bikarhêneriyê, dabînkirina fonksiyona malperê û bibîranîna tercîhên we, em çerezan (cookies) bi kar tînin.
          </p>
        </div>

        {/* Section 1 */}
        <section id="cerez-nedir" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
            Çerez Çi ne û Çima Tên Bikaranîn?
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Çerez, pelên piçûk ên nivîsê ne ku gava hûn serdana malperekê dikin bi rêya geroka we li ser cîhaza we (kompîtur, smartphone, tablet) tên tomarkirin.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Çerez dihêlin ku malper bi bandortir bixebite, zimanê we yê hilbijartî an agahdariya danişînê bi bîr tîne û ezmûnek gerokê ya kesane pêşkêş dike.
          </p>
        </section>

        {/* Section 2 */}
        <section id="cerez-turleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
            Cûreyên Çerezên ku li ser Malpera Me Tên Bikaranîn
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Malpera me tenê çerezên teknîkî û analîtîk ên pêwîst bi kar tîne:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
                🔒
              </div>
              <h4 className="font-bold text-brand-purple-900 text-base">1. Çerezên Pêwîst / Teknîkî</h4>
              <p className="text-xs text-gray-500 leading-relaxed font-light">
                Ji bo fonksiyonên bingehîn û ewlehiya malperê, û her weha parastina zimanê we yê hilbijartî (Kurdî, Tirkî, Erebî, Îngilîzî) pêwîst in. Heke werin neçalak kirin, dibe ku hin beşên malperê bi rêkûpêk nexebitin.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
                📊
              </div>
              <h4 className="font-bold text-brand-purple-900 text-base">2. Çerezên Analîtîk</h4>
              <p className="text-xs text-gray-500 leading-relaxed font-light">
                Bêyî berhevkirina agahiyên nasnameya kesane, alîkariya me dikin ku em fêm bikin bê ka mêvan çawa bi awayekî nenas (anonîm) bi malper û raporên me re dikevin têkiliyê.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section id="cerez-listesi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
            Lîsteya Berfireh a Çerezan û Erkên Wan
          </h2>
          <div className="rounded-2xl border border-brand-purple-100/80 overflow-hidden shadow-sm bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-brand-purple-900 text-white font-serif text-xs uppercase tracking-wider">
                  <tr>
                    <th className="py-4 px-6 w-1/4">Navê Çerezê</th>
                    <th className="py-4 px-6 w-1/6">Kategorî</th>
                    <th className="py-4 px-6">Erk</th>
                    <th className="py-4 px-6 w-1/5">Dema Mayînê</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-600 font-light text-xs">
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">i18nextLng</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                        Pêwîst
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">Zimanê ku we hilbijartiye diparêze da ku ezmûna we ya gerokê hevgirtî be.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">1 Sal</td>
                  </tr>
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">_ga / _gid</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200/60 rounded-full font-semibold text-[11px]">
                        Analîtîk
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">Statîstîkên bikaranîna nenas bi rêya Google Analytics.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">2 Sal / 24 Saet</td>
                  </tr>
                  <tr className="hover:bg-brand-purple-50/40 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">session_id</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                        Pêwîst
                      </span>
                    </td>
                    <td className="py-4 px-6 leading-relaxed">Ewlehî û berdewamiya danişîna gerokê misoger dike.</td>
                    <td className="py-4 px-6 font-medium text-gray-700">Danişîn</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="cerez-yonetimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
            Birêvebirin û Astengkirina Vebijêrkên Çerezan
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Hûn dikarin tercîhên çerezan bi rêya mîhengên geroka xwe biguherînin, jê bibin an bi tevahî asteng bikin:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Google Chrome</h4>
              <p className="text-xs text-gray-500 font-light">
                Settings → Privacy & Security → Cookies and other site data.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Mozilla Firefox</h4>
              <p className="text-xs text-gray-500 font-light">
                Settings → Privacy & Security → Cookies and Site Data.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Apple Safari</h4>
              <p className="text-xs text-gray-500 font-light">
                Preferences → Privacy → Block all cookies.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
              <h4 className="font-bold text-brand-purple-900 text-sm">Microsoft Edge</h4>
              <p className="text-xs text-gray-500 font-light">
                Settings → Cookies and site permissions → Cookies and data stored.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section id="politikadaki-degisiklikler" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
            Nûvekirinên Siyasetê û Têkilî
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Em dikarin vê Siyaseta Çerezan li gorî pêdiviyên qanûnî û teknîkî nûve bikin. Hemû guhertin gava li ser malperê hatin weşandin derbasdar dibin.
          </p>
          <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-brand-purple-900 text-sm">Ji bo pirsên li ser çerezan:</h4>
              <p className="text-xs text-gray-500 font-light">Hûn dikarin bi rêya e-peyamê bi me re têkilî daynin.</p>
            </div>
            <a
              href="mailto:tja.bilgi@gmail.com"
              className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
            >
              tja.bilgi@gmail.com
            </a>
          </div>
        </section>
      </LegalLayout>
    );
  }

  // Default: Turkish
  const sections: TocSection[] = [
    { id: 'cerez-nedir', title: '1. Çerez (Cookie) Nedir?' },
    { id: 'cerez-turleri', title: '2. Sitemizde Kullanılan Çerez Türleri' },
    { id: 'cerez-listesi', title: '3. Detaylı Çerez Listesi ve İşlevleri' },
    { id: 'cerez-yonetimi', title: '4. Çerez Tercihlerinin Yönetilmesi ve Engellenmesi' },
    { id: 'politikadaki-degisiklikler', title: '5. Politika Güncellemeleri ve İletişim' },
  ];

  return (
    <LegalLayout
      title="Çerez Politikası"
      subtitle="Çerez Bilgilendirmesi ve Yönetim Rehberi"
      lastUpdated="01 Eylül 2026"
      activeTab="cookies"
      sections={sections}
    >
      {/* Intro Note */}
      <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
        <div className="flex items-center space-x-3 font-bold text-base">
          <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
            🍪
          </span>
          <span>Çerez Bilgilendirmesi</span>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed font-light">
          TJA olarak, web sitemizi ziyaretiniz sırasında kullanıcı deneyiminizi geliştirmek, site işlevselliğini sağlamak ve tercihlerinizi hatırlamak amacıyla çerezler (cookies) kullanmaktayız.
        </p>
      </div>

      {/* Section 1 */}
      <section id="cerez-nedir" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
          Çerez (Cookie) Nedir ve Neden Kullanılır?
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Çerezler, bir web sitesini ziyaret ettiğinizde tarayıcınız aracılığıyla cihazınıza (bilgisayar, akıllı telefon, tablet) kaydedilen küçük metin dosyalarıdır.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          Çerezler, web sitesinin daha verimli çalışmasını sağlar, seçtiğiniz dili veya oturum bilgilerinizi hatırlar ve kişiselleştirilmiş bir gezinme deneyimi sunar.
        </p>
      </section>

      {/* Section 2 */}
      <section id="cerez-turleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
          Sitemizde Kullanılan Çerez Türleri
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Web sitemizde yalnızca temel işlevsellik ve kullanım performansına yönelik çerezler kullanılmaktadır:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
              🔒
            </div>
            <h4 className="font-bold text-brand-purple-900 text-base">1. Zorunlu / Teknik Çerezler</h4>
            <p className="text-xs text-gray-500 leading-relaxed font-light">
              Web sitemizin düzgün çalışması, güvenliğinin sağlanması ve seçtiğiniz dil tercihinin (Türkçe, İngilizce, Arapça, Kürtçe) hatırlanması için kesinlikle gerekli çerezlerdir. Bu çerezler engellendiğinde sitenin bazı kısımları çalışmayabilir.
            </p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-brand-purple-100 shadow-sm space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center font-bold text-sm">
              📊
            </div>
            <h4 className="font-bold text-brand-purple-900 text-base">2. Analitik ve Performans Çerezleri</h4>
            <p className="text-xs text-gray-500 leading-relaxed font-light">
              Ziyaretçilerin siteyi nasıl kullandığını (en çok okunan haberler, ziyaret süreleri) anonim olarak analiz etmemizi sağlar. Kişisel kimlik tespiti yapılmaz, yalnızca genel istatistiki veriler toplanır.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section id="cerez-listesi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
          Detaylı Çerez Listesi ve İşlevleri
        </h2>
        <div className="rounded-2xl border border-brand-purple-100/80 overflow-hidden shadow-sm bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-brand-purple-900 text-white font-serif text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-4 px-6 w-1/4">Çerez Adı</th>
                  <th className="py-4 px-6 w-1/6">Kategori</th>
                  <th className="py-4 px-6">İşlevi</th>
                  <th className="py-4 px-6 w-1/5">Saklama Süresi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-600 font-light text-xs">
                <tr className="hover:bg-brand-purple-50/40 transition-colors">
                  <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">i18nextLng</td>
                  <td className="py-4 px-6">
                    <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                      Zorunlu
                    </span>
                  </td>
                  <td className="py-4 px-6 leading-relaxed">Seçtiğiniz dil tercihini hafızada tutar.</td>
                  <td className="py-4 px-6 font-medium text-gray-700">1 Yıl</td>
                </tr>
                <tr className="hover:bg-brand-purple-50/40 transition-colors">
                  <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">_ga / _gid</td>
                  <td className="py-4 px-6">
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200/60 rounded-full font-semibold text-[11px]">
                      Analitik
                    </span>
                  </td>
                  <td className="py-4 px-6 leading-relaxed">Google Analytics anonim kullanım istatistikleri.</td>
                  <td className="py-4 px-6 font-medium text-gray-700">2 Yıl / 24 Saat</td>
                </tr>
                <tr className="hover:bg-brand-purple-50/40 transition-colors">
                  <td className="py-4 px-6 font-mono font-bold text-brand-purple-900">session_id</td>
                  <td className="py-4 px-6">
                    <span className="inline-block px-3 py-1 bg-brand-purple-100 text-brand-purple-800 rounded-full font-semibold text-[11px]">
                      Zorunlu
                    </span>
                  </td>
                  <td className="py-4 px-6 leading-relaxed">Güvenli oturum sürekliliğini sağlar.</td>
                  <td className="py-4 px-6 font-medium text-gray-700">Oturum Süresince</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section id="cerez-yonetimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
          Çerez Tercihlerinin Yönetilmesi ve Engellenmesi
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Çerez tercihlerinizi dilediğiniz zaman tarayıcı ayarlarınız üzerinden değiştirebilir, çerezleri tamamen engelleyebilir veya silebilirsiniz. Tarayıcı bazlı adımlar:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
            <h4 className="font-bold text-brand-purple-900 text-sm">Google Chrome</h4>
            <p className="text-xs text-gray-500 font-light">
              Ayarlar → Gizlilik ve Güvenlik → Çerezler ve Diğer Site Verileri.
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
            <h4 className="font-bold text-brand-purple-900 text-sm">Mozilla Firefox</h4>
            <p className="text-xs text-gray-500 font-light">
              Seçenekler → Gizlilik ve Güvenlik → Çerezler ve Site Verileri.
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
            <h4 className="font-bold text-brand-purple-900 text-sm">Apple Safari</h4>
            <p className="text-xs text-gray-500 font-light">
              Tercihler → Gizlilik → Tüm Çerezleri Engelle.
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
            <h4 className="font-bold text-brand-purple-900 text-sm">Microsoft Edge</h4>
            <p className="text-xs text-gray-500 font-light">
              Ayarlar → Site İzinleri → Çerezler ve Site Verileri.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5 */}
      <section id="politikadaki-degisiklikler" className="scroll-mt-32 space-y-4">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
          Politika Güncellemeleri ve İletişim
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Çerez Politikamız kanuni değişiklikler ve teknik ihtiyaçlar doğrultusunda güncellenebilir. Güncel metin sitede yayınlandığı andan itibaren yürürlüğe girer.
        </p>
        <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-brand-purple-900 text-sm">Çerez Sorularınız İçin:</h4>
            <p className="text-xs text-gray-500 font-light">Çerezler ile ilgili her türlü soru için bize ulaşabilirsiniz.</p>
          </div>
          <a
            href="mailto:tja.bilgi@gmail.com"
            className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
          >
            tja.bilgi@gmail.com
          </a>
        </div>
      </section>
    </LegalLayout>
  );
};

export default CookiePolicy;
